// src/api/authApi.js

import axiosInstance from './axiosInstance';
import { toast } from 'react-toastify';
import config from '../config/env';
import { authStateManager } from './authStateManager';

const API_URL = config.API_URL;

const authApi = {
  setAuthToken: (token) => {
    console.log('authApi: setAuthToken called', { hasToken: !!token });
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
      authStateManager.setActive();
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  },

  setupInterceptors: () => {
    // No longer needed as interceptors are set up in axiosInstance
  },

  logout: (showNotification = true) => {
    console.log('authApi: logout called', {
      showNotification,
      isExpiredOrLoggedOut: authStateManager.isExpiredOrLoggedOut()
    });
    
    if (!authStateManager.isExpiredOrLoggedOut()) {
      authApi.setAuthToken(null);
      if (showNotification) {
        authStateManager.handleLogout();
      }
    }
  },

  login: async (email, password) => {
    try {
      const response = await axiosInstance.post(`/login`, { email, password });
      const jwt = response.data.results.jwt;
      authApi.setAuthToken(jwt);
      toast.success('Login successful');
      return jwt;
    } catch (error) {
      throw error;
    }
  },

  checkAuthStatus: () => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.setAuthToken(token);
      return true;
    }
    return false;
  },

  register: async (name, email, password, terms, newsletter, invitationCode) => {
    try {
      const response = await axiosInstance.post(`/register`, { 
        name, 
        email, 
        password, 
        terms, 
        newsletter,
        invitationCode
      });
      toast.success('Registration successful');
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(errorKey => {
          switch (errorKey) {
            case 'INVALID_EMAIL_FORMAT':
              return 'Invalid email format';
            case 'INVALID_PASSWORD_FORMAT':
              return 'Invalid password format';
            case 'TERMS_NOT_CONFIRMED':
              return 'You must agree to the terms and conditions';
            case 'USER_EXISTS':
              return 'This email is already registered';
            case 'INVALID_INVITATION_CODE':
              return 'Invalid invitation code';
            default:
              return 'Registration failed';
          }
        });
        const errorMessage = errorMessages.join('. ');
        toast.error(errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    }
  },

  confirmEmail: async (email, emailConfirmationToken) => {
    try {
      const response = await axiosInstance.post(`/confirmation`, { email, emailConfirmationToken });
      toast.success('Email confirmed successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Email confirmation failed');
      throw error;
    }
  },

  resetPassword: async (email) => {
    try {
      const response = await axiosInstance.post(`/reset`, { email });
      toast.success('Password reset email sent. Please check your inbox.');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPasswordConfirm: async (email, passwordResetToken) => {
    try {
      const response = await axiosInstance.post(`/resetconfirm`, { email, passwordResetToken });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetPasswordFinal: async (email, password, passwordResetToken) => {
    try {
      const response = await axiosInstance.post(`/resetfinal`, { email, password, passwordResetToken });
      toast.success('Password reset successful. You can now log in with your new password.');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

};

export default authApi;